import { Modal } from '@round/whitelabel-ui';
import { Button } from '@round/whitelabel-ui';
import styles from './PurchaseOrderAllocationModal.module.css';
import { creatorbase } from '@round/api';
import { Badge } from '@round/whitelabel-ui';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import usePurchaseOrderAllocations, { PurchaseOrderAllocation } from './usePurchaseOrderAllocations';
import PurchaseOrderInfo from './components/PurchaseOrderInfo/PurchaseOrderInfo';
import PurchaseOrderAllocations from './components/PurchaseOrderAllocations/PurchaseOrderAllocations';
import useCampaignSelect, { CampaignOption } from '../../../Campaigns/hooks/useCampaignSelect';
import useCampaignAllocations, { CampaignPOAllocation } from './useCampaignAllocations';
import CampaignInfo from './components/CampaignInfo/CampaignInfo';
import CampaignAllocations from './components/CampaignAllocations/CampaignAllocations';
import { asMoneyWithoutZeros, showNotification } from 'helpers';
import { useEffect, useState } from 'react';
import DeleteAllocation from './components/DeleteAllocation/DeleteAllocation';
import CampaignSelect from './components/CampaignSelect/CampaignSelect';
import { useCreatePurchaseOrderLine } from '@round/creatorbase-helpers/api-hooks';
import usePurchaseOrderSelect, { PurchaseOrderOption } from '../hooks/usePurchaseOrderSelect';
import PurchaseOrderSelect from './components/PurchaseOrderSelect/PurchaseOrderSelect';
import { CurrencyInput } from '@round/whitelabel-ui';
import cn from 'classnames';
import { useAllocationsInvalidations } from './useAllocationsInvalidations';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';

const getMaxAllocationAmount = (
    purchaseOrder: creatorbase.PurchaseOrder | PurchaseOrderOption | null | undefined,
    campaign: creatorbase.Campaign | CampaignOption | null | undefined
) => {
    return Math.min(
        (campaign?.cost ?? 0) - (campaign?.purchase_orders_allocated_amount ?? 0),
        purchaseOrder?.remaining_amount ?? 0
    );
};

type Props = {
    isOpen: boolean;
    onClose: (haveAllocationsBeenWritten?: boolean) => void;
} & ({ purchaseOrderId: string | null | undefined } | { campaignId: number | null | undefined });

const PurchaseOrderAllocationModal = ({ isOpen, onClose, ...props }: Props) => {
    const { selectedClientId } = useClientSelect();

    const { getCurrencyById } = useCurrencies();
    const [poSelectParams, setPoSelectParams] = useState<Pick<
        creatorbase.GetPurchaseOrdersParams,
        'exclude_allocated_to_campaign_id' | 'currency_id'
    > | null>(null);

    const {
        getProps: getPurchaseOrderSelectProps,
        value: selectedPurchaseOrder,
        resetValue: resetSelectedPurchaseOrder,
    } = usePurchaseOrderSelect({
        initialValueId: 'purchaseOrderId' in props ? props.purchaseOrderId : null,
        params: {
            exclude_fully_allocated: true,
            client_id: selectedClientId,
            exclude_allocated_to_campaign_id: 'campaignId' in props ? props.campaignId?.toString() : undefined,
            ...poSelectParams,
        },
        enabled: isOpen && typeof selectedClientId === 'number',
    });

    const {
        getProps: getCampaignSelectProps,
        value: selectedCampaign,
        resetValue: resetSelectedCampaign,
    } = useCampaignSelect({
        initialValueId: 'campaignId' in props ? props.campaignId : null,
        params: {
            exclude_fully_allocated: true,
            currency_id: selectedPurchaseOrder?.currency_id,
            exclude_allocated_to_purchase_id: selectedPurchaseOrder?.id,
            client_id: selectedClientId,
        },
        enabled: isOpen && typeof selectedClientId === 'number',
    });

    useEffect(() => {
        if (selectedCampaign && selectedCampaign.id) {
            setPoSelectParams({
                currency_id: selectedCampaign.currency_id,
                exclude_allocated_to_campaign_id: selectedCampaign.id.toString(),
            });
        }
    }, [selectedCampaign]);

    const {
        purchaseOrder,
        isPurchaseOrderLoading,
        errorMessage: poErrorMessage,
        allocations,
        allocationsErrorMessage,
        areAllocationsLoading,
        hasNextPage: hasNextPurchaseOrderAllocationsPage,
        fetchNextPage: fetchNextPurchaseOrderAllocationsPage,
    } = usePurchaseOrderAllocations(selectedPurchaseOrder?.id, { enabled: isOpen });

    const {
        campaign,
        isCampaignLoading,
        campaignErrorMessage,
        allocations: campaignAllocations,
        areAllocationsLoading: areCampaignAllocationsLoading,
        allocationsErrorMessage: campaignAllocationsErrorMessage,
        hasNextPage: hasNextCampaignAllocationsPage,
        fetchNextPage: fetchNextCampaignAllocationsPage,
    } = useCampaignAllocations(selectedCampaign?.id, { enabled: isOpen });

    const [haveAllocationsBeenWritten, setHaveAllocationsBeenWritten] = useState(false);
    const handleClose = () => {
        resetSelectedCampaign();
        resetSelectedPurchaseOrder();
        onClose(haveAllocationsBeenWritten);
        setHaveAllocationsBeenWritten(false);
    };

    const { invalidateAllocations } = useAllocationsInvalidations();

    const refreshData = (allocation: creatorbase.PurchaseOrderLine) => {
        invalidateAllocations({
            purchaseOrderId: allocation.purchase_order_id,
            campaignId: allocation.campaign_id,
        });
    };

    const [allocationAmount, setAllocationAmount] = useState('');
    const maxAllocationAmount = getMaxAllocationAmount(purchaseOrder, campaign);

    const allocationCurrency = getCurrencyById(purchaseOrder?.currency_id || campaign?.currency_id);

    const {
        create: createAllocation,
        isPending: isAllocationLoading,
        helpers: createAllocationHelpers,
    } = useCreatePurchaseOrderLine();

    const handleAllocate = async () => {
        if (!purchaseOrder || !campaign || !allocationAmount) {
            return;
        }

        createAllocation(
            { purchase_order_id: purchaseOrder.id, campaign_id: campaign.id, amount: Number(allocationAmount) },
            {
                onSuccess: (allocation) => {
                    setHaveAllocationsBeenWritten(true);
                    refreshData(allocation);
                    setAllocationAmount('');
                    showNotification('Success', 'info');
                },
                onError: (error) => {
                    showNotification(createAllocationHelpers.getErrorMessage(error), 'error');
                },
            }
        );
    };

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [allocationToDelete, setAllocationToDelete] = useState<CampaignPOAllocation | PurchaseOrderAllocation | null>(
        null
    );

    return (
        <>
            <Modal className={styles.modal} closeOnOverlayClick closeOnEscape isOpen={isOpen} onClose={handleClose}>
                <Modal.Header>
                    <Modal.Title>Campaign Allocation</Modal.Title>
                </Modal.Header>

                <Modal.Body className={styles.body}>
                    <section className={styles.section}>
                        <p className={styles.sectionTitle}>
                            Campaign&nbsp;
                            {campaign ? (
                                <Badge className={styles.currencyBadge}>
                                    {getCurrencyById(campaign.currency_id)?.name}
                                </Badge>
                            ) : null}
                        </p>

                        <CampaignSelect
                            {...getCampaignSelectProps({
                                onChange: (campaignOption) => {
                                    const newMaxAllocationAmount = getMaxAllocationAmount(
                                        purchaseOrder,
                                        campaignOption
                                    );
                                    setAllocationAmount(
                                        newMaxAllocationAmount ? newMaxAllocationAmount.toString() : ''
                                    );
                                },
                                isDisabled: 'campaignId' in props,
                            })}
                        />

                        {campaign && (
                            <CampaignInfo
                                campaign={campaign}
                                isLoading={isCampaignLoading}
                                error={campaignErrorMessage}
                            />
                        )}

                        {campaign && (
                            <CampaignAllocations
                                campaign={campaign}
                                allocations={campaignAllocations}
                                isLoading={areCampaignAllocationsLoading}
                                error={campaignAllocationsErrorMessage}
                                hasNextPage={hasNextCampaignAllocationsPage}
                                fetchNextPage={fetchNextCampaignAllocationsPage}
                                onUpdated={(allocation) => {
                                    setHaveAllocationsBeenWritten(true);
                                    refreshData(allocation);
                                }}
                                onDelete={(allocation) => {
                                    setAllocationToDelete(allocation);
                                    setIsDeleteModalOpen(true);
                                }}
                            />
                        )}
                    </section>

                    <section className={styles.allocationAmountSection}>
                        <label htmlFor="allocationAmount" className={styles.allocationAmountLabel}>
                            Allocation Amount
                        </label>
                        <CurrencyInput
                            className={cn(styles.allocationAmountInput, styles.input)}
                            id="allocationAmount"
                            max={maxAllocationAmount}
                            value={allocationAmount}
                            onValueChange={(value) => setAllocationAmount(value ?? '')}
                            prefix={allocationCurrency?.symbol ?? ''}
                        />
                        <p className={styles.maxAllocationAmount}>
                            Max: {asMoneyWithoutZeros(maxAllocationAmount, allocationCurrency)}
                        </p>
                    </section>

                    <section className={styles.section}>
                        <p className={styles.sectionTitle}>
                            Purchase Order&nbsp;
                            {purchaseOrder ? (
                                <Badge className={styles.currencyBadge}>
                                    {getCurrencyById(purchaseOrder.currency_id)?.name}
                                </Badge>
                            ) : null}
                        </p>

                        <PurchaseOrderSelect
                            {...getPurchaseOrderSelectProps({
                                onChange: (poOption) => {
                                    const newMaxAllocationAmount = getMaxAllocationAmount(poOption, campaign);
                                    setAllocationAmount(
                                        newMaxAllocationAmount ? newMaxAllocationAmount.toString() : ''
                                    );
                                },
                                isDisabled: 'purchaseOrderId' in props,
                            })}
                        />

                        {purchaseOrder && (
                            <PurchaseOrderInfo
                                purchaseOrder={purchaseOrder}
                                isLoading={isPurchaseOrderLoading}
                                error={poErrorMessage}
                            />
                        )}

                        {purchaseOrder && (
                            <PurchaseOrderAllocations
                                purchaseOrder={purchaseOrder}
                                allocations={allocations}
                                isLoading={areAllocationsLoading}
                                error={allocationsErrorMessage}
                                hasNextPage={hasNextPurchaseOrderAllocationsPage}
                                fetchNextPage={fetchNextPurchaseOrderAllocationsPage}
                                onUpdated={(allocation) => {
                                    setHaveAllocationsBeenWritten(true);
                                    refreshData(allocation);
                                }}
                                onDelete={(allocation) => {
                                    setAllocationToDelete(allocation);
                                    setIsDeleteModalOpen(true);
                                }}
                            />
                        )}
                    </section>
                </Modal.Body>

                <Modal.Actions className={styles.actions}>
                    <Button appearance="ghost" type="button" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        appearance="primary"
                        type="button"
                        isLoading={isAllocationLoading}
                        disabled={
                            !purchaseOrder?.remaining_amount ||
                            (!selectedCampaign && !selectedPurchaseOrder) ||
                            Number(allocationAmount) > maxAllocationAmount ||
                            !allocationAmount
                        }
                        onClick={handleAllocate}
                    >
                        Create Allocation
                    </Button>
                </Modal.Actions>
            </Modal>

            <DeleteAllocation
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setAllocationToDelete(null);
                }}
                allocation={allocationToDelete}
                onDeleted={(allocation) => {
                    setHaveAllocationsBeenWritten(true);
                    refreshData(allocation);
                }}
            />
        </>
    );
};

export default PurchaseOrderAllocationModal;
